import { ROAD_LABEL_CLASSES } from './roadHierarchy';
import type {
  ExpressionSpecification,
  StyleSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import type { BasemapConfiguration } from './catalog';
import {
  DEFAULT_LABEL_APPEARANCE,
  boundLabelHalo,
  labelEmphasisColour,
  labelHaloBaseline,
  labelHaloColour,
  labelHaloFactor,
  scaleLabelValue,
  type BasemapLabelGroup,
} from './labelAppearance';
import {
  resolvedBasemapLabelPresentation,
  BASEMAP_LABEL_GROUPS,
  type ResolvedBasemapLabelPresentation,
} from './labelPresentation';

/** Curated provider glyph faces. Medium/Semibold are deliberately not requested (404). */
export const BASEMAP_FONT = {
  regular: ['Noto Sans Regular'],
  bold: ['Noto Sans Bold'],
  italic: ['Noto Sans Italic'],
} as const;
const rank: ExpressionSpecification = ['to-number', ['coalesce', ['get', 'rank'], 100], 100];
const capital: ExpressionSpecification = ['to-number', ['coalesce', ['get', 'capital'], 100], 100];
const settlement: ExpressionSpecification = [
  'match',
  ['get', 'class'],
  ['city', 'town'],
  true,
  false,
];
export const PLACE_PRIMARY: ExpressionSpecification = [
  'all',
  settlement,
  ['any', ['<=', rank, 3], ['all', ['>=', capital, 2], ['<=', capital, 4]]],
];
export const PLACE_SECONDARY: ExpressionSpecification = [
  'all',
  settlement,
  ['!', PLACE_PRIMARY],
  ['any', ['==', ['get', 'class'], 'city'], ['<=', rank, 12]],
];
export const PLACE_LOCAL: ExpressionSpecification = [
  'all',
  ['!', PLACE_PRIMARY],
  ['!', PLACE_SECONDARY],
  ['match', ['get', 'class'], ['town', 'village', 'borough', 'suburb'], true, false],
];
export const PLACE_MINOR: ExpressionSpecification = [
  'match',
  ['get', 'class'],
  ['hamlet', 'quarter', 'neighbourhood', 'isolated_dwelling'],
  true,
  false,
];
// OpenMapTiles settlement classes, separate from physical islands and major cities.
const contextualPlace: ExpressionSpecification = [
  'match',
  ['get', 'class'],
  ['village', 'borough', 'suburb', 'hamlet', 'quarter', 'neighbourhood', 'isolated_dwelling'],
  true,
  false,
];
const places: ExpressionSpecification = [
  'any',
  PLACE_PRIMARY,
  PLACE_SECONDARY,
  PLACE_LOCAL,
  PLACE_MINOR,
];
const majorRoad: ExpressionSpecification = [
  'match',
  ['get', 'class'],
  [...ROAD_LABEL_CLASSES.motorwaysHighways],
  true,
  false,
];
const arterial: ExpressionSpecification = [
  'match',
  ['get', 'class'],
  [...ROAD_LABEL_CLASSES.arterialRoads],
  true,
  false,
];
const collector: ExpressionSpecification = [
  '==',
  ['get', 'class'],
  ROAD_LABEL_CLASSES.collectorRoads[0],
];
const country: ExpressionSpecification = ['==', ['get', 'class'], 'country'];
const region: ExpressionSpecification = [
  'match',
  ['get', 'class'],
  ['state', 'province'],
  true,
  false,
];
const island: ExpressionSpecification = [
  'match',
  ['get', 'class'],
  ['island', 'islet'],
  true,
  false,
];
const significantIsland: ExpressionSpecification = [
  'all',
  ['==', ['get', 'class'], 'island'],
  ['<=', rank, 3],
];
const supportingIsland: ExpressionSpecification = [
  'all',
  ['==', ['get', 'class'], 'island'],
  ['<=', rank, 5],
];

/** Detect positive class tests only; excluded classes in residual filters are not roles. */
function positiveClass(value: unknown, name: string): boolean {
  if (!Array.isArray(value)) return false;
  if (value[0] === '!' || value[0] === '!=' || value[0] === '!in') return false;
  if (JSON.stringify(value[1]) === '["get","class"]') {
    if (value[0] === '==') return value[2] === name;
    if (value[0] === 'match')
      return (
        (value[2] === name || (Array.isArray(value[2]) && value[2].includes(name))) &&
        value[3] === true
      );
    if (value[0] === 'in') return JSON.stringify(value[2]).includes(`"${name}"`);
  }
  return value.slice(1).some((part) => positiveClass(part, name));
}
function labelGroup(layer: SymbolLayerSpecification): BasemapLabelGroup {
  const group = (layer.metadata as Record<string, unknown> | undefined)?.['placeio:labelGroup'];
  return BASEMAP_LABEL_GROUPS.find((value) => value === group) ?? 'otherLabels';
}
function replaceStateClass(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  if (value[0] === '==' && JSON.stringify(value[1]) === '["get","class"]' && value[2] === 'state')
    return region;
  return value.map(replaceStateClass);
}
function geographicVisibility(profile: ResolvedBasemapLabelPresentation): ExpressionSpecification {
  const minorStart =
    profile === 'detailed' ? 13 : profile === 'minimal' || profile === 'thematic' ? 16 : 14;
  return [
    'step',
    ['zoom'],
    ['!', ['any', region, island]],
    // Symbol filters run at integer tile zoom; minzoom gates the visible layer at z3.5.
    3,
    ['any', ['!', ['any', region, island]], ['all', region, ['<=', rank, 3]]],
    5,
    ['any', ['!', island], significantIsland],
    9,
    ['all', ['!', region], ['any', ['!', island], significantIsland]],
    11,
    ['all', ['!', region], ['any', ['!', island], supportingIsland]],
    minorStart,
    ['!', region],
  ];
}

export function placeLabelZooms(
  profile: ResolvedBasemapLabelPresentation,
): readonly [number, number, number, number] {
  if (profile === 'detailed') return [3, 6, 10, 12];
  if (profile === 'minimal') return [3, 9, 14, 16];
  if (profile === 'thematic') return [3, 7, 13, 16];
  return [3, 7, 11, 13];
}

function placeValue(
  primary: unknown,
  secondary: unknown,
  local: unknown,
  minor: unknown,
  broad: unknown = local,
): ExpressionSpecification {
  return [
    'case',
    PLACE_PRIMARY,
    primary,
    PLACE_SECONDARY,
    secondary,
    PLACE_LOCAL,
    local,
    PLACE_MINOR,
    minor,
    broad,
  ] as ExpressionSpecification;
}
function zoomSize(
  low: ExpressionSpecification | number,
  city: ExpressionSpecification | number,
  close: ExpressionSpecification | number,
): ExpressionSpecification {
  return ['interpolate', ['linear'], ['zoom'], 5, low, 11, city, 16, close];
}
function gets(value: unknown, key: string): boolean {
  return (
    Array.isArray(value) &&
    ((value[0] === 'get' && value[1] === key) || value.some((part) => gets(part, key)))
  );
}
function constrain(layer: SymbolLayerSpecification, predicate: ExpressionSpecification): void {
  layer.filter = ['all', layer.filter ?? true, predicate] as ExpressionSpecification;
}
function tierVisibility(profile: ResolvedBasemapLabelPresentation): ExpressionSpecification {
  const [primary, secondary, local, minor] = placeLabelZooms(profile);
  // A filter removes ineligible symbols from placement; opacity alone would still cause collisions.
  return [
    'step',
    ['zoom'],
    ['!', places],
    primary,
    ['any', ['!', places], PLACE_PRIMARY],
    secondary,
    ['any', ['!', places], PLACE_PRIMARY, PLACE_SECONDARY],
    local,
    ['any', ['!', places], PLACE_PRIMARY, PLACE_SECONDARY, PLACE_LOCAL],
    minor,
    true,
  ];
}

/** One typography boundary shared by the adapter, inset and every static basemap pass. */
export function applyBasemapLabelTypography(
  style: StyleSpecification,
  configuration: BasemapConfiguration,
): void {
  const family = configuration.presetId;
  if (family === 'none') return;
  const profile = resolvedBasemapLabelPresentation(configuration, family);
  const dark = family === 'dropmap-dark';
  const streets = family === 'dropmap-streets';
  const thematic = profile === 'thematic';
  const appearance =
    configuration.customizationByPreset[family].labelAppearance ?? DEFAULT_LABEL_APPEARANCE;
  const emphasis = (value: string) => labelEmphasisColour(value, appearance.emphasis, dark);
  const colour = emphasis(dark ? '#e2e6ea' : streets ? '#303b43' : '#35434b');
  const quiet = emphasis(dark ? '#aab6c0' : '#68777e');
  const secondaryQuiet = emphasis(dark ? '#9aa8b4' : '#78858b');
  const primaryScale = profile === 'minimal' ? 1.07 : thematic ? 1.02 : 1;
  const supportScale =
    profile === 'detailed' ? 0.94 : thematic ? 0.92 : profile === 'muted' ? 0.97 : 1;
  for (const layer of style.layers) {
    if (layer.type !== 'symbol' || layer.layout?.['text-field'] === undefined) continue;
    const source = layer['source-layer'];
    const group = labelGroup(layer);
    const halo = labelHaloColour(group, dark);
    const baseline = labelHaloBaseline(group, profile);
    const providerOpacity = layer.paint?.['text-opacity'] ?? 1;
    if (gets(layer.layout['text-field'], 'ref') && layer.layout['icon-image'] !== undefined) {
      layer.layout['text-size'] = scaleLabelValue(layer.layout['text-size'] ?? 16, appearance.size);
      layer.layout['icon-size'] = scaleLabelValue(layer.layout['icon-size'] ?? 1, appearance.size);
      continue;
    }
    layer.layout = { ...layer.layout, 'text-font': [...BASEMAP_FONT.regular] };
    layer.paint = {
      ...layer.paint,
      'text-color': colour,
      'text-halo-color': halo,
      'text-halo-width': baseline,
      'text-halo-blur': appearance.halo === 'off' ? 0 : 0.2,
    };
    if (source === 'place') {
      const isCountry = positiveClass(layer.filter, 'country');
      const isRegion =
        positiveClass(layer.filter, 'state') || positiveClass(layer.filter, 'province');
      layer.metadata = {
        ...(layer.metadata as object),
        'placeio:labelRole': isCountry
          ? 3
          : isRegion
            ? 2
            : labelGroup(layer) === 'otherLabels'
              ? 0
              : 1,
      };
      if (isRegion) {
        layer.filter = replaceStateClass(layer.filter) as ExpressionSpecification;
        layer.minzoom = 3.5;
        layer.maxzoom = 9;
      } else if (isCountry) layer.maxzoom = Math.min(layer.maxzoom ?? 7, 7);
      else if (labelGroup(layer) === 'otherLabels' && !positiveClass(layer.filter, 'continent')) {
        // Province labels use the regional layer; this avoids duplicate provider residuals.
        constrain(layer, ['!', region]);
        layer.minzoom = Math.min(layer.minzoom ?? 5, 5);
      }
      if (profile === 'detailed' && (layer.minzoom ?? 0) >= 11)
        layer.minzoom = Math.max(10, layer.minzoom! - 1);
      constrain(layer, tierVisibility(profile));
      constrain(layer, geographicVisibility(profile));
      constrain(layer, ['step', ['zoom'], true, 7, ['!', country]]);
      if (profile === 'minimal' || thematic)
        constrain(layer, ['any', places, country, region, significantIsland]);
      layer.layout = {
        ...layer.layout,
        'text-font': [
          'step',
          ['zoom'],
          ['literal', [...BASEMAP_FONT.regular]],
          6,
          placeValue(
            ['literal', [...BASEMAP_FONT.bold]],
            ['literal', [...BASEMAP_FONT.regular]],
            [
              'case',
              contextualPlace,
              ['literal', [...BASEMAP_FONT.italic]],
              ['literal', [...BASEMAP_FONT.regular]],
            ],
            ['literal', [...BASEMAP_FONT.italic]],
          ),
        ],
        'text-size': zoomSize(
          placeValue(12 * primaryScale, 12 * supportScale, 10 * supportScale, 9 * supportScale, 10),
          placeValue(
            22 * primaryScale,
            15 * supportScale,
            12 * supportScale,
            10 * supportScale,
            11,
          ),
          placeValue(
            25 * primaryScale,
            17 * supportScale,
            14 * supportScale,
            11 * supportScale,
            12,
          ),
        ),
        'text-transform': ['case', contextualPlace, 'uppercase', 'none'],
        'text-letter-spacing': ['case', contextualPlace, 0.06, placeValue(0, 0, 0, 0.015, 0.06)],
        'symbol-sort-key': ['+', placeValue(0, 1000, 2000, 3000, 5000), rank],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-padding': 3,
      };
      layer.paint = {
        ...layer.paint,
        'text-color': placeValue(
          colour,
          colour,
          profile === 'detailed' || thematic ? secondaryQuiet : quiet,
          secondaryQuiet,
          quiet,
        ),
        'text-opacity': providerOpacity,
        'text-halo-width': zoomSize(
          // National type is smaller. Stay below the glyph halo limit even at 80% size/Strong.
          placeValue(
            Math.min(labelHaloBaseline('citiesTowns', profile), 1.3),
            labelHaloBaseline('citiesTowns', profile) * 0.7,
            labelHaloBaseline('suburbsLocalities', profile),
            labelHaloBaseline('suburbsLocalities', profile) * 0.7,
            labelHaloBaseline('otherLabels', profile),
          ),
          placeValue(
            labelHaloBaseline('citiesTowns', profile),
            labelHaloBaseline('citiesTowns', profile) * 0.7,
            labelHaloBaseline('suburbsLocalities', profile),
            labelHaloBaseline('suburbsLocalities', profile) * 0.7,
            labelHaloBaseline('otherLabels', profile),
          ),
          placeValue(
            labelHaloBaseline('citiesTowns', profile),
            labelHaloBaseline('citiesTowns', profile) * 0.7,
            labelHaloBaseline('suburbsLocalities', profile),
            labelHaloBaseline('suburbsLocalities', profile) * 0.7,
            labelHaloBaseline('otherLabels', profile),
          ),
        ),
      };
      // Dark upstream stopped primary city labels at z12; keep centres at local/export scales.
      if (dark && !isCountry && !isRegion && layer.maxzoom !== undefined && layer.maxzoom >= 12)
        delete layer.maxzoom;
      if (isCountry) {
        // Keep the approved world Standard backing; category adjustments still apply on demand.
        layer.paint['text-halo-width'] = 0.7;
        layer.paint['text-halo-color'] = dark ? 'rgba(24,29,35,0.90)' : 'rgba(250,251,248,0.88)';
        layer.paint['text-halo-blur'] = appearance.halo === 'off' ? 0 : 0.35;
        // Preserve Standard world typography below z3; strengthen national orientation thereafter.
        layer.layout['text-size'] = [
          'interpolate',
          ['linear'],
          ['zoom'],
          2,
          11,
          3,
          15,
          4,
          20 * primaryScale,
          5,
          22 * primaryScale,
          6,
          20,
        ];
        layer.paint['text-color'] = ['step', ['zoom'], quiet, 3, colour];
        layer.layout['text-font'] = [
          'step',
          ['zoom'],
          ['literal', [...BASEMAP_FONT.regular]],
          3,
          ['literal', [...BASEMAP_FONT.bold]],
        ];
        layer.layout['text-letter-spacing'] = 0.06;
      } else if (isRegion) {
        layer.layout['text-size'] = ['interpolate', ['linear'], ['zoom'], 3.5, 12, 5, 13, 8, 12];
        layer.paint['text-color'] = quiet;
        layer.paint['text-opacity'] = [
          'interpolate',
          ['linear'],
          ['zoom'],
          3.5,
          0.82,
          5,
          0.92,
          8,
          0.65,
          9,
          0,
        ];
        layer.layout['text-transform'] = 'uppercase';
        layer.layout['text-font'] = [...BASEMAP_FONT.regular];
        layer.layout['text-letter-spacing'] = 0.08;
        layer.paint['text-halo-width'] = 0.65;
      } else if (positiveClass(layer.filter, 'continent')) {
        layer.layout['text-size'] = zoomSize(11, 14, 16);
      }
    } else if (source === 'transportation_name') {
      if (gets(layer.layout['text-field'], 'ref')) {
        // Plain references have no graphic backing and follow ordinary family-aware text protection.
        layer.layout['text-size'] = 10;
        layer.paint['text-color'] = quiet;
      } else {
        if (positiveClass(layer.filter, 'rail') || positiveClass(layer.filter, 'railway')) continue;
        layer.metadata = {
          ...(typeof layer.metadata === 'object' && layer.metadata !== null ? layer.metadata : {}),
          'placeio:roadPriority':
            positiveClass(layer.filter, 'motorway') || positiveClass(layer.filter, 'trunk')
              ? 0
              : positiveClass(layer.filter, 'primary') || positiveClass(layer.filter, 'secondary')
                ? 1
                : positiveClass(layer.filter, 'tertiary')
                  ? 2
                  : 3,
        };
        const offset = streets ? -1 : 0;
        const detail =
          (profile === 'detailed' ? -1 : 0) +
          (appearance.roadDensity === 'high' ? -1 : appearance.roadDensity === 'low' ? 1 : 0);
        const start = 12 + offset + detail;
        layer.minzoom = Math.max(layer.minzoom ?? 0, start);
        constrain(layer, [
          'step',
          ['zoom'],
          false,
          start,
          majorRoad,
          13 + offset + detail,
          ['any', majorRoad, arterial],
          13.5 + offset + detail,
          ['any', majorRoad, arterial, collector],
          14 + offset + detail,
          true,
        ]);
        layer.layout = {
          ...layer.layout,
          'text-size': zoomSize(
            ['case', majorRoad, 11, arterial, 10.5, collector, 10, 9.5],
            ['case', majorRoad, 11.5, arterial, 11, collector, 10.5, 9.5],
            ['case', majorRoad, 12.5, arterial, 11.5, collector, 11, 10.5],
          ),
          'text-font': [...BASEMAP_FONT.regular],
          'text-letter-spacing': 0,
          'symbol-spacing':
            appearance.roadDensity === 'low'
              ? 550
              : appearance.roadDensity === 'high'
                ? 150
                : profile === 'detailed'
                  ? 200
                  : 250,
          'text-padding':
            appearance.roadDensity === 'high' ? 1 : appearance.roadDensity === 'low' ? 4 : 2,
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'symbol-sort-key': ['case', majorRoad, 0, arterial, 1, collector, 2, 3],
        };
        if (appearance.roadTiers)
          constrain(layer, [
            'case',
            majorRoad,
            appearance.roadTiers.motorwaysHighways,
            arterial,
            appearance.roadTiers.arterialRoads,
            collector,
            appearance.roadTiers.collectorRoads,
            ['match', ['get', 'class'], [...ROAD_LABEL_CLASSES.localRoads], true, false],
            appearance.roadTiers.localRoads,
            true,
          ]);
        layer.paint['text-color'] = ['case', majorRoad, quiet, secondaryQuiet];
        layer.paint['text-opacity'] = 0.82;
        layer.paint['text-halo-width'] = baseline;
      }
    } else if (source === 'water_name' || source === 'waterway') {
      layer.layout = {
        ...layer.layout,
        'text-font': [...BASEMAP_FONT.italic],
        'text-size': zoomSize(10, 11, 12),
        'text-letter-spacing': 0.06,
        'text-transform': 'none',
      };
      layer.paint = {
        ...layer.paint,
        'text-color': emphasis(dark ? '#91b0bf' : '#607f8d'),
        'text-opacity': 0.82,
        'text-halo-width': baseline,
      };
      if (source === 'waterway')
        layer.minzoom = Math.max(layer.minzoom ?? 0, profile === 'detailed' ? 12 : 13);
      if (source === 'water_name')
        constrain(layer, [
          'step',
          ['zoom'],
          ['match', ['get', 'class'], ['bay', 'strait'], false, true],
          12,
          true,
        ]);
    } else {
      const poi = source === 'poi';
      layer.layout = {
        ...layer.layout,
        'text-size': zoomSize(10, 11, 12),
        'text-letter-spacing': 0,
      };
      layer.paint = {
        ...layer.paint,
        'text-color': quiet,
        'text-opacity': 0.8,
        'text-halo-width': baseline,
      };
      if (poi && profile === 'detailed') layer.minzoom = Math.max(14, (layer.minzoom ?? 15) - 1);
      if (!poi && (profile === 'minimal' || thematic)) constrain(layer, ['==', ['literal', 1], 0]);
    }
    layer.layout['text-size'] = scaleLabelValue(layer.layout['text-size'] ?? 16, appearance.size);
    layer.paint['text-halo-width'] = scaleLabelValue(
      layer.paint['text-halo-width'] ?? 0,
      labelHaloFactor(appearance.halo, group, profile),
    );
    if (appearance.halo === 'maximum' || appearance.size < 0.8)
      layer.paint['text-halo-width'] = boundLabelHalo(
        layer.paint['text-halo-width']!,
        layer.layout['text-size']!,
      );
  }
  const roadLayers = style.layers.filter(
    (layer) =>
      (layer.metadata as Record<string, unknown> | undefined)?.['placeio:roadPriority'] !==
      undefined,
  );
  roadLayers.sort(
    (a, b) =>
      Number((b.metadata as Record<string, unknown>)['placeio:roadPriority']) -
      Number((a.metadata as Record<string, unknown>)['placeio:roadPriority']),
  );
  let roadIndex = 0;
  style.layers = style.layers.map((layer) =>
    (layer.metadata as Record<string, unknown> | undefined)?.['placeio:roadPriority'] !== undefined
      ? roadLayers[roadIndex++]!
      : layer,
  );
  prioritizePlaceLabels(style);
}

function prioritizePlaceLabels(style: StyleSpecification): void {
  // Preserve all provider order within each role. Topmost settlement layers place before POIs/roads.
  const settlementLayers = style.layers.filter(
    (layer): layer is SymbolLayerSpecification =>
      layer.type === 'symbol' &&
      layer['source-layer'] === 'place' &&
      layer.layout?.['text-field'] !== undefined,
  );
  settlementLayers.sort((a, b) => {
    const priority = (layer: typeof a) =>
      Number((layer.metadata as Record<string, unknown>)['placeio:labelRole']);
    return priority(a) - priority(b);
  });
  style.layers = [
    ...style.layers.filter((layer) => !settlementLayers.some((place) => place === layer)),
    ...settlementLayers,
  ];
}
